// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  anularInfraccion,
  crearInfraccion,
  crearSancion,
  traerInfracciones,
  traerKmExcedidos,
} from "../services/infracciones";
import { traerChoferes } from "../services/rrhh";
import { FLOTABOT } from "../components/layout/secciones";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { rutaDePersona } from "../shared/personas";
import { UMBRAL, VENTANA_MESES, estadoDe } from "../shared/infracciones";
import { hoyAr } from "../shared/fechaAr";

/* Infracciones (#118, pedido de Erick): el contador con consecuencias.
 *
 * ── EL FLUJO ES «CONTAR Y AVISAR», NUNCA «EJECUTAR» ─────────────────────────
 *
 * Se cargan infracciones; a la 4.ª dentro de la ventana el chofer queda
 * marcado «corresponde apercibimiento» — acá en rojo, y por mail a Erick, que
 * lo manda el bot con la misma regla—. El apercibimiento y la suspensión los
 * registra el clic de una persona con `cargar.rrhh`: ésa es la firma. La
 * regla entera (12 meses corridos, reinicio con la sanción) vive en
 * `shared/infracciones`, con el porqué de cada default.
 *
 * ── AGRUPADA POR PERSONA, NO CRONOLÓGICA ────────────────────────────────────
 *
 * La pregunta de esta pantalla es «¿quién está cerca del límite?», no «¿qué
 * pasó ayer?». Por eso ordena por contador y el detalle de cada persona se
 * abre al tocarla. Lo cronológico ya lo tiene la ficha.
 *
 * -- EL DOCUMENTO SE ABRE EN EL BOT, Y ES A PROPOSITO (#121) ----------------
 *
 * El texto del apercibimiento y de la suspensión son modelos legales que pasó
 * Erick, y viven en UN solo lugar: `sancion_texto.py`. Un gemelo en
 * JavaScript —como sí tiene el contador— serviría para una regla, pero acá lo
 * que se duplicaría es el texto que firma la empresa, y dos copias que un día
 * difieren son dos versiones del mismo papel circulando.
 *
 * El link sólo aparece si la infracción trae `episodio`. Las cargadas a mano
 * no lo tienen: el papel saldría con todas las rayas, y ofrecerlo igual sería
 * prometer un documento armado que no está armado.
 */

const HOY = () => hoyAr(); // el «hoy» argentino, no el de UTC (fechaAr.js)

const fecha = (v) => {
  const s = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const Infracciones = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  const puedeCargar = puede("cargar.rrhh");
  // Lo ve RRHH y también tráfico (P-165, Nico): son infracciones de manejo de
  // los choferes. La pared real es la RLS de la base; esto es la pantalla.
  // Cargar y sancionar siguen pidiendo cargar.rrhh (más abajo), así que tráfico
  // mira pero no toca.
  const permitido =
    permisos !== null &&
    (permisos.includes("ver.rrhh") || permisos.includes("ver.trafico"));

  const [datos, setDatos] = useState(undefined); // undefined = cargando
  const [kmDias, setKmDias] = useState([]);      // días de más de 800 km
  const [error, setError] = useState(false);
  const [choferes, setChoferes] = useState([]);
  const [abierto, setAbierto] = useState(null); // dni desplegado
  // Filtro de la LISTA (P-166, Nico): buscar por nombre/DNI para ver si alguien
  // reincidió, sin salir a la ficha ni traer el legajo entero. Es distinto del
  // buscador del alta (`buscar`), que elige a quién cargarle una infracción.
  const [filtroLista, setFiltroLista] = useState("");

  // El alta.
  const [dni, setDni] = useState("");
  const [buscar, setBuscar] = useState("");
  const [f, setF] = useState(HOY());
  const [detalle, setDetalle] = useState("");
  const [origen, setOrigen] = useState("");
  const [estado, setEstado] = useState(""); // "" | yendo | mal:<msg>

  const cargarTodo = () => {
    traerInfracciones()
      .then(setDatos)
      .catch(() => setError(true));
  };

  useEffect(() => {
    if (!permitido) return;
    cargarTodo();
    traerChoferes()
      .then(setChoferes)
      .catch(() => setChoferes([]));
    traerKmExcedidos()
      .then(setKmDias)
      .catch(() => setKmDias([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido]);

  const nombreDe = useMemo(() => {
    const m = new Map();
    for (const c of choferes)
      m.set(String(c.dni), `${c.apellido}, ${c.nombres}`);
    return m;
  }, [choferes]);

  const candidatos = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (q.length < 2) return [];
    return choferes
      .filter((c) =>
        [c.apellido, c.nombres, c.dni, c.legajo]
          .map((v) => String(v ?? "").toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 8);
  }, [choferes, buscar]);

  /* Una tarjeta por persona, con SU contador. La regla corre acá por dni y
   * es la misma que corre el bot para el mail — `shared/infracciones`. */
  const porPersona = useMemo(() => {
    if (!datos) return [];
    const hoy = HOY();
    const mapa = new Map();
    for (const i of datos.infracciones) {
      if (!mapa.has(i.dni)) mapa.set(i.dni, { inf: [], san: [] });
      mapa.get(i.dni).inf.push(i);
    }
    for (const s of datos.sanciones) {
      if (!mapa.has(s.dni)) mapa.set(s.dni, { inf: [], san: [] });
      mapa.get(s.dni).san.push(s);
    }
    return [...mapa.entries()]
      .map(([d, { inf, san }]) => ({
        dni: d,
        nombre:
          nombreDe.get(String(d)) ||
          inf.find((x) => x.chofer_nombre)?.chofer_nombre ||
          san.find((x) => x.chofer_nombre)?.chofer_nombre ||
          `DNI ${d}`,
        inf,
        san,
        estado: estadoDe(inf, san, hoy),
      }))
      // Los que corresponden arriba; después por contador; después por nombre.
      .sort(
        (a, b) =>
          Number(b.estado.corresponde) - Number(a.estado.corresponde) ||
          b.estado.total - a.estado.total ||
          a.nombre.localeCompare(b.nombre),
      );
  }, [datos, nombreDe]);

  // P-166: el filtro de la lista, aplicado a las dos tablas de la pantalla —el
  // contador por persona y los días de +800 km—, que es donde Nico quería ver
  // la reincidencia. Sobre lo ya cargado, sin pegarle a la base.
  const q166 = filtroLista.trim().toLowerCase();
  const coincide = (txt) => !q166 || String(txt ?? "").toLowerCase().includes(q166);
  const personasVis = q166
    ? porPersona.filter((p) => coincide(p.nombre) || coincide(p.dni))
    : porPersona;
  const kmDiasVis = q166
    ? kmDias.filter((k) => coincide(k.chofer) || coincide(k.interno))
    : kmDias;

  const guardar = async (e) => {
    e.preventDefault();
    const d = dni.replace(/\D/g, "");
    if (!d) return setEstado("mal:Falta la persona (elegila o escribí el DNI).");
    if (!detalle.trim())
      return setEstado("mal:Falta el detalle: qué pasó. Sin eso, la fila no se puede defender después.");
    setEstado("yendo");
    try {
      await crearInfraccion({
        dni: d,
        chofer_nombre: nombreDe.get(d) || buscar,
        fecha: f,
        detalle,
        origen,
      });
      setEstado("");
      setDni("");
      setBuscar("");
      setDetalle("");
      setOrigen("");
      setF(HOY());
      cargarTodo();
    } catch (err) {
      setEstado(
        "mal:" +
          (/policy|permission|denied|violates/i.test(err?.message || "")
            ? "No se cargó: no tenés permiso."
            : `No se pudo guardar: ${err?.message || "error"}`),
      );
    }
  };

  /* La firma: el clic que registra la sanción. Con confirmación del
   * navegador porque no hay vuelta silenciosa — anular queda registrado. */
  const sancionar = async (p, tipo) => {
    const rotulo = tipo === "apercibimiento" ? "APERCIBIMIENTO" : "SUSPENSIÓN";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Registrar ${rotulo} a ${p.nombre}? El contador se reinicia.`))
      return;
    try {
      await crearSancion({
        dni: p.dni,
        chofer_nombre: p.nombre,
        tipo,
        fecha: HOY(),
        nota: `Por ${p.estado.total} infracciones en ${VENTANA_MESES} meses`,
      });
      cargarTodo();
    } catch {
      setEstado("mal:No se pudo registrar la sanción. Probá de nuevo.");
    }
  };

  const anular = async (id) => {
    try {
      await anularInfraccion(id);
      cargarTodo();
    } catch {
      setEstado("mal:No se pudo anular. Probá de nuevo en un rato.");
    }
  };

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      {puedeCargar && (
        <form className="tk-filtros" onSubmit={guardar} style={{ rowGap: 8 }}>
          <div style={{ position: "relative" }}>
            <input
              type="search"
              placeholder="Chofer (nombre, DNI o legajo)"
              value={buscar}
              onChange={(e) => {
                setBuscar(e.target.value);
                setDni("");
              }}
              aria-label="Buscar al chofer"
            />
            {!dni && candidatos.length > 0 && (
              <div className="lic-sugeridos">
                {candidatos.map((c) => (
                  <button
                    key={c.dni}
                    type="button"
                    onClick={() => {
                      setDni(String(c.dni));
                      setBuscar(`${c.apellido}, ${c.nombres}`);
                    }}
                  >
                    {c.apellido}, {c.nombres}{" "}
                    <span className="sub">
                      DNI {c.dni} · {c.destino || "sin base"}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!dni && /^\d{6,9}$/.test(buscar.replace(/\D/g, "")) &&
              candidatos.length === 0 && (
                <button
                  type="button"
                  className="tk-chip"
                  onClick={() => setDni(buscar.replace(/\D/g, ""))}
                >
                  Usar el DNI {buscar.replace(/\D/g, "")}
                </button>
              )}
          </div>
          <label className="sub">
            fecha{" "}
            <input type="date" value={f} onChange={(e) => setF(e.target.value)} />
          </label>
          <input
            type="text"
            placeholder="Qué pasó (obligatorio)"
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            aria-label="Detalle"
          />
          <input
            type="text"
            placeholder="Origen (multa, parte, …)"
            value={origen}
            onChange={(e) => setOrigen(e.target.value)}
            aria-label="Origen"
          />
          <button
            type="submit"
            className="btn-primario"
            disabled={estado === "yendo"}
          >
            {estado === "yendo" ? "Guardando…" : "Cargar infracción"}
          </button>
          {estado.startsWith("mal:") && (
            <span className="pill mal">{estado.slice(4)}</span>
          )}
        </form>
      )}

      {!puedeCargar && (
        <p className="panel-aviso suave">
          Podés mirar, pero no cargar.
        </p>
      )}

      {/* EL ENSAYO. El alta automática desde los excesos de velocidad está
          APAGADA (#121, pedido de Igna: mirarlo unos días antes). Mientras
          tanto se puede ver qué cargaría y a quién le movería el contador.
          El link va acá arriba y no escondido: un ensayo que hay que
          acordarse de buscar es un ensayo que no se corre. */}
      {puedeCargar && (
        <p className="panel-aviso suave">
          El alta automática desde los excesos de velocidad está{" "}
          <b>apagada</b>. Antes de prenderla se puede ver qué cargaría y a
          quién le movería el contador:{" "}
          <a
            className="rto-link"
            href={`${FLOTABOT}/sancion/uat`}
            target="_blank"
            rel="noreferrer"
          >
            UAT · simulación de sanciones
          </a>
          . No escribe nada.
        </p>
      )}

      {datos !== undefined && porPersona.length === 0 && (
        <div className="panel-aviso suave">
          <h3>No hay ninguna infracción cargada</h3>
        </div>
      )}

      {(porPersona.length > 0 || kmDias.length > 0) && (
        <div className="tk-filtros" style={{ marginTop: 8 }}>
          <input
            type="search"
            placeholder="Filtrar la lista por nombre o DNI…"
            value={filtroLista}
            onChange={(e) => setFiltroLista(e.target.value)}
            aria-label="Filtrar infracciones por nombre o DNI"
            style={{ minWidth: 260 }}
          />
        </div>
      )}

      {porPersona.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Chofer</th>
                <th>Contador ({VENTANA_MESES} meses)</th>
                <th>Última sanción</th>
                <th>Estado</th>
                {puedeCargar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {personasVis.length === 0 && (
                <tr>
                  <td colSpan={puedeCargar ? 5 : 4} className="tenue">
                    Ningún chofer coincide con «{filtroLista.trim()}».
                  </td>
                </tr>
              )}
              {personasVis.map((p) => (
                <BloquePersona
                  key={p.dni}
                  p={p}
                  abierto={abierto === p.dni}
                  onAbrir={() =>
                    setAbierto(abierto === p.dni ? null : p.dni)
                  }
                  puedeCargar={puedeCargar}
                  onSancionar={sancionar}
                  onAnular={anular}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {kmDias.length > 0 && (
        <>
          <h3 style={{ marginTop: 28 }}>
            Más de 800 km en un día · {kmDiasVis.length} caso(s)
            {q166 && kmDiasVis.length !== kmDias.length
              ? ` (de ${kmDias.length})`
              : ""}
          </h3>
          <div className="tabla-wrap">
            <table className="tabla fija">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Interno</th>
                  <th>Km</th>
                  <th>Chofer</th>
                </tr>
              </thead>
              <tbody>
                {kmDiasVis.length === 0 && (
                  <tr>
                    <td colSpan={4} className="tenue">
                      Ningún caso coincide con «{filtroLista.trim()}».
                    </td>
                  </tr>
                )}
                {kmDiasVis.map((k) => (
                  <tr key={`${k.fecha}-${k.interno}`}>
                    <td>{fecha(k.fecha)}</td>
                    <td>
                      <Link to={`/unidad/tractor/${k.interno}`}>
                        {k.interno}
                      </Link>
                    </td>
                    <td>
                      <b>{Number(k.km).toLocaleString("es-AR")}</b>
                    </td>
                    <td>
                      {k.chofer || (
                        <span className="tk-meta">
                          — sin viaje que lo cubra
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

/* Los links al documento de UNA infracción.
 *
 * El apercibimiento se abre derecho: no hay nada que decidir, el texto sale
 * entero del episodio. La suspensión pide DÍAS y DESDE — no salen de la
 * telemetría, son la decisión de quien firma — y por eso se piden acá y no se
 * inventan. Se piden en la fila y no en un popup, como todo el resto del hub.
 *
 * Las otras dos fechas del modelo (último día y día de retomar) NO se piden:
 * las calcula `sancion_texto` desde esas dos. Pedir tres fechas que tienen que
 * cerrar entre sí es abrir la puerta al error que después se discute.
 */
const Documento = ({ inf }) => {
  const [abierto, setAbierto] = useState(false);
  const [dias, setDias] = useState("");
  const [desde, setDesde] = useState("");

  if (!inf.episodio) return null;
  const base = `${FLOTABOT}/sancion/${inf.id}`;
  const listo = Number(dias) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(desde);

  return (
    <>
      <a
        className="rto-link"
        style={{ marginLeft: 8 }}
        href={`${base}?tipo=apercibimiento`}
        target="_blank"
        rel="noreferrer"
      >
        Apercibimiento
      </a>
      <button
        className="tk-boton tk-boton--chico tk-boton--flojo"
        style={{ marginLeft: 8 }}
        onClick={() => setAbierto(!abierto)}
      >
        Suspensión…
      </button>
      {abierto && (
        <span style={{ marginLeft: 8 }}>
          <input
            type="number"
            min="1"
            max="30"
            value={dias}
            onChange={(e) => setDias(e.target.value)}
            placeholder="días"
            aria-label="Días de suspensión"
            style={{ width: 70 }}
          />
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            aria-label="Desde qué día se cumple"
            style={{ marginLeft: 6 }}
          />
          {/* Deshabilitado hasta que estén los dos: un documento con
              «_______ (CERO) días» impreso es peor que uno que no salió. */}
          <a
            className="rto-link"
            style={{
              marginLeft: 8,
              pointerEvents: listo ? "auto" : "none",
              opacity: listo ? 1 : 0.45,
            }}
            href={`${base}?tipo=suspension&dias=${dias}&desde=${desde}`}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!listo}
          >
            Abrir documento
          </a>
        </span>
      )}
    </>
  );
};

/* La fila de una persona + su detalle desplegable. El detalle marca cuáles
 * infracciones CUENTAN y cuáles no (fuera de ventana, o tapadas por una
 * sanción): sin eso, el contador es un número que nadie puede discutir. */
const BloquePersona = ({ p, abierto, onAbrir, puedeCargar, onSancionar, onAnular }) => {
  const cuentanIds = new Set(p.estado.cuentan.map((i) => i.id));
  const ultimaSan = [...p.san]
    .filter((s) => !s.anulado_en)
    .sort((a, b) => (String(a.fecha) < String(b.fecha) ? 1 : -1))[0];
  return (
    <>
      <tr className={p.estado.corresponde ? "mal" : ""}>
        <td>
          <Link className="rto-link" to={rutaDePersona(p.dni)}>
            {p.nombre}
          </Link>
        </td>
        <td>
          <b>{p.estado.total}</b> de {UMBRAL}
        </td>
        <td>
          {ultimaSan ? (
            <>
              {ultimaSan.tipo === "suspension" ? "Suspensión" : "Apercibimiento"}{" "}
              <span className="sub">{fecha(ultimaSan.fecha)}</span>
            </>
          ) : (
            <span className="tenue">—</span>
          )}
        </td>
        <td>
          {p.estado.corresponde ? (
            <span className="pill mal">
              Corresponde apercibimiento — lo firma RRHH
            </span>
          ) : (
            <span className="pill gris">al día</span>
          )}
        </td>
        {puedeCargar && (
          <td className="agenda-acc">
            {p.estado.corresponde && (
              <>
                <button
                  className="tk-boton tk-boton--chico"
                  onClick={() => onSancionar(p, "apercibimiento")}
                >
                  Registrar apercibimiento
                </button>
                <button
                  className="tk-boton tk-boton--chico tk-boton--flojo"
                  onClick={() => onSancionar(p, "suspension")}
                >
                  Registrar suspensión
                </button>
              </>
            )}
            <button className="tk-boton tk-boton--chico tk-boton--flojo" onClick={onAbrir}>
              {abierto ? "Cerrar" : `Ver ${p.inf.length}`}
            </button>
          </td>
        )}
        {!puedeCargar && null}
      </tr>
      {abierto && (
        <tr>
          <td colSpan={puedeCargar ? 5 : 4}>
            <div className="tk-item-meta" style={{ padding: "6px 0" }}>
              {p.inf
                .filter((i) => !i.anulado_en)
                .map((i) => (
                  <div key={i.id} style={{ padding: "3px 0" }}>
                    {fecha(i.fecha)} — {i.detalle}
                    {i.origen ? ` · ${i.origen}` : ""}
                    {i.descargo ? " · con descargo" : ""}
                    {!cuentanIds.has(i.id) && (
                      <em>
                        {" "}
                        (no cuenta:{" "}
                        {p.estado.ultimaSancion &&
                        String(i.fecha).slice(0, 10) <= p.estado.ultimaSancion
                          ? "anterior a la última sanción"
                          : "fuera de la ventana"}
                        )
                      </em>
                    )}
                    {puedeCargar && <Documento inf={i} />}
                    {puedeCargar && (
                      <button
                        className="tk-boton tk-boton--chico tk-boton--flojo"
                        style={{ marginLeft: 8 }}
                        onClick={() => onAnular(i.id)}
                      >
                        Anular
                      </button>
                    )}
                  </div>
                ))}
              {p.inf.filter((i) => !i.anulado_en).length === 0 && (
                <em>Sin infracciones vigentes.</em>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default Infracciones;
